import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Activity, FileText, CalendarClock, Users, LayoutGrid, MessageSquare, Phone, Video, ArrowUpRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const iconMap = {
  rcm: Activity, ehr: FileText, pms: CalendarClock, hrms: Users,
  backoffice: LayoutGrid, chat: MessageSquare, call: Phone, video: Video,
};
const routeMap = { ehr: "/dashboard/ehr", rcm: "/dashboard/rcm" };

export default function Overview() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="font-mono text-xs text-[#93A5B5] uppercase tracking-[0.2em] animate-pulse">Loading…</div>;

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <h1 className="font-display text-3xl md:text-5xl font-medium tracking-tighter text-[#0C2A40]">
          Welcome back, <span className="font-extrabold text-[#2880B8]">{(user?.name || "").split(" ")[0]}.</span>
        </h1>
        <p className="mt-3 text-[#51667A]">Your revenue cycle at a glance.</p>
      </motion.div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { v: data.stats.clean_claim, l: "Clean Claim Ratio" },
          { v: data.stats.ar_days, l: "Day AR Cycle" },
          { v: data.stats.net_collection, l: "Net Collection" },
          { v: data.stats.tat, l: "Charge-to-Claim" },
        ].map((s) => (
          <div key={s.l} className="bg-white rounded-2xl border border-[#D8E6F1] p-6">
            <div className="font-mono text-3xl md:text-4xl font-bold text-[#0C2A40] tracking-tight">{s.v}</div>
            <div className="mt-2 font-mono text-[10px] tracking-[0.12em] uppercase text-[#93A5B5]">{s.l}</div>
          </div>
        ))}
      </div>

      <h2 className="mt-12 font-mono text-[11px] tracking-[0.2em] uppercase text-[#51667A]">/ Your Modules</h2>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {data.modules.map((m, i) => {
          const Icon = iconMap[m.key] || LayoutGrid;
          const to = routeMap[m.key];
          const Wrapper = to ? Link : "div";
          return (
            <motion.div
              key={m.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04, duration: 0.4 }}
            >
              <Wrapper
                {...(to ? { to } : {})}
                data-testid={`dash-module-${m.key}`}
                className={`group block h-full bg-white rounded-2xl border p-6 transition-all duration-500 ${to ? "border-[#2880B8]/40 hover:border-[#2880B8] hover:shadow-[0_24px_50px_-24px_rgba(40,128,184,0.4)] cursor-pointer" : "border-[#D8E6F1] cursor-default"}`}
              >
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl bg-[#E7F1F8] flex items-center justify-center group-hover:bg-[#2880B8] transition-colors duration-500">
                    <Icon size={22} strokeWidth={1.5} className="text-[#2880B8] group-hover:text-white transition-colors duration-500" />
                  </div>
                  <span className={`font-mono text-[9px] tracking-[0.12em] uppercase px-2 py-1 rounded-full ${to ? "text-white bg-[#2880B8]" : m.status === "active" ? "text-[#1F6EA1] bg-[#E7F1F8]" : "text-[#93A5B5] bg-[#F2F8FC]"}`}>
                    {to ? "Open" : m.status}
                  </span>
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold text-[#0C2A40]">{m.name}</h3>
                <p className="mt-2 text-sm text-[#51667A] leading-relaxed min-h-[60px]">{m.desc}</p>
                <div className="mt-4 pt-4 border-t border-[#D8E6F1] flex items-center justify-between">
                  <div>
                    <div className="font-mono text-sm font-bold text-[#0C2A40]">{m.metric}</div>
                    <div className="font-mono text-[9px] tracking-[0.12em] uppercase text-[#93A5B5]">{m.metric_label}</div>
                  </div>
                  <ArrowUpRight size={16} className="text-[#93A5B5] group-hover:text-[#2880B8] transition-colors" />
                </div>
              </Wrapper>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
